




































































import { Component, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { Context } from '@nuxt/types';

import { HasPageNumber } from '~/interfaces/pagination';
import { HasFilterDefinition, FilterableView } from '~/interfaces/filters';

import { SupplierCollection } from '~/types/Supplier';
import { SupplierOverviewEntry } from '~/types/SupplierOverview';
import { FilterDefinition, FilterOptionDefinition } from '~/types/FilterDefinition';
import { ProductSolutionEntry } from '~/types/Product';

import SupplierOverview from '~/api/contentful/supplier-overview';
import ProductIntentionApi from '~/api/contentful/product-intention';

import { setMunicipalityCookie, setMunicipalityState } from '~/helpers/municipalityState';
import { cookieName } from '~/store/municipality';

import PageLayout from '~/patterns/organisms/page-layout/page-layout.vue';

import ActiveFilters from '~/patterns/molecules/active-filters.vue';
import CardSupplier from '~/patterns/molecules/_cards/card-supplier/card-supplier.vue';
import ContentBlocks from '~/patterns/organisms/_collections/content-blocks/content-blocks.vue';
import Filterable from '~/patterns/organisms/filterable.vue';
import Filters from '~/patterns/organisms/filters/filters.vue';
import GhButton from '~/patterns/atoms/button/button.vue';
import Modal from '~/patterns/organisms/modal/modal.vue';
import ModalOpener from '~/patterns/molecules/modal-opener/modal-opener.vue';
import { Location } from '~/store/location';
import Pagination from '~/patterns/molecules/pagination/pagination.vue';
import RichText from '~/patterns/molecules/rich-text/rich-text.vue';

const EditButton = () => import(/* webpackPrefetch: true */ '~/patterns/molecules/edit-button.vue');

// Pagination settings need to be defined outside the class component,
// as fetch() and asyncData() don't have access to 'this'.
const PAGINATION = {
    pageNumber: 1,
    perPage: 6,
    filters: [] as FilterDefinition[]
};

// The @Component decorator indicates the class is a Vue component
@Component({
    components: {
        ActiveFilters,
        CardSupplier,
        ContentBlocks,
        EditButton,
        Filterable,
        Filters,
        GhButton,
        Modal,
        ModalOpener,
        PageLayout,
        Pagination,
        RichText
    },

    computed: {
        ...mapGetters({
            activeFilters: 'supplier-filters/activeFilters',
            suppliers: 'suppliers/suppliers',
            totalPages: 'suppliers/totalPages',
            userLocation: 'location/location'
        })
    },
    beforeMount() {
        document.addEventListener('keydown', () => {
            const tooltip = document.querySelector('.tooltip.show');
            tooltip && tooltip.classList.remove('show');
        });
    },
    watch: {
        activeFilters: {
            deep: true,
            immediate: false,
            handler: function(this: VakSpecialisten, value) {
                this.loading = true;
                if (this.$route.query && this.$route.query.page && value === PAGINATION.filters) {
                    PAGINATION.pageNumber = this.pageNumber = Number(this.$route.query.page);
                }
                // 'Remember' active filters for fetch
                PAGINATION.filters = value;
                return this.$store
                    .dispatch('suppliers/getPaginatedSuppliers', {
                        pagination: {
                            pageNumber: this.pageNumber || 1,
                            perPage: PAGINATION.perPage,
                            userLocation: this.userLocation
                        },
                        filters: value
                    })
                    .then(() => {
                        this.loading = false;
                    })
                    .catch(err => {
                        if (this.$bugsnag) {
                            this.$bugsnag.notify(err);
                        }
                        this.loading = false;
                    });
            }
        },
        pageNumber: {
            immediate: false,
            handler: function(this: VakSpecialisten) {
                this.loading = true;
                return this.$store
                    .dispatch('suppliers/getPaginatedSuppliers', {
                        pagination: { pageNumber: this.pageNumber || 1, perPage: PAGINATION.perPage },
                        filters: this.activeFilters
                    })
                    .then(() => {
                        this.loading = false;
                    })
                    .catch(err => {
                        if (this.$bugsnag) {
                            this.$bugsnag.notify(err);
                        }
                        this.loading = false;
                    });
            }
        }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetch({ store, query }): Promise<any> | void {
        PAGINATION.pageNumber = Number(query.page) || PAGINATION.pageNumber || 1;
        const { pageNumber, perPage, filters } = PAGINATION;
        return store.dispatch('suppliers/getPaginatedSuppliers', {
            pagination: { pageNumber, perPage },
            filters
        });
    },

    asyncData(context: Context): Promise<void | { entry: SupplierOverviewEntry }> {
        const { error } = context;
        setMunicipalityCookie(context);

        return new SupplierOverview()
            .getFirst()
            .then(entry => {
                if (!entry) {
                    error({ statusCode: 404, message: 'Pagina niet gevonden' });
                }
                return { entry };
            })
            .catch(() => {
                error({ statusCode: 500, message: 'Er ging iets mis' });
            });
    },

    head(this: VakSpecialisten): object {
        return {
            title: 'Vakspecialisten'
        };
    }
})
class VakSpecialisten extends Vue implements HasPageNumber, HasFilterDefinition, FilterableView {
    entry: SupplierOverviewEntry | null = null;

    filterDefinition: FilterDefinition[] = [];
    activeFilters: FilterDefinition[];
    userLocation: Location;

    isMunicipalityPage: boolean = false;
    municipality = null;

    pageNumber: number = PAGINATION.pageNumber;

    loading: boolean = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $bugsnag: any;

    // XXX: mapped getters need to be in the component state in order to access them.
    suppliers: SupplierCollection;

    get hasItems(): boolean {
        return this.suppliers && this.suppliers.items && this.suppliers.items.length > 0;
    }

    get supplierOverview() {
        return this.entry && this.entry.fields;
    }

    /**
     * Get the Filter definition based on the settings of the overview entry
     */
    getfilterDefinition(): Promise<FilterDefinition[]> {
        const definitions: FilterDefinition[] = [];

        if (this.entry && this.entry.fields.ratingFilter) {
            definitions.push(this.getRatingFilterDefinition());
        }

        if (this.entry && this.entry.fields.productSolutionfilter) {
            return this.getSolutionFilterDefinition().then(filterDefinition => {
                definitions.push(filterDefinition);
                return definitions;
            });
        }

        return Promise.resolve(definitions);
    }

    getRatingFilterDefinition(): FilterDefinition {
        const options: FilterOptionDefinition[] = [];
        for (let i = 1; i <= 5; i++) {
            const stars = i === 1 ? 'ster' : 'sterren';
            options.push({ key: `rating-${i}`, name: `Minimaal ${i} ${stars}`, value: i });
        }
        return {
            id: 'minimum-rating',
            name: 'Minimale beoordeling',
            options: options,
            field: 'averageRating', // TODO: filter on average review ratings (linked entries)
            operator: '[gte]',
            type: 'stars',
            showButton: true
        };
    }

    /**
     * Build a filter definition containing all available product solutions and underlying opportunities
     */
    // TODO: move to helper?
    getSolutionFilterDefinition(): Promise<FilterDefinition> {
        return new ProductIntentionApi().getAll().then(
            (entries): FilterDefinition => {
                const options: FilterOptionDefinition[] = [];
                for (const intention of entries.items) {
                    if (!intention.fields || !intention.fields.solutions) {
                        continue;
                    }

                    for (let solution of Object.values(intention.fields.solutions)) {
                        solution = solution as ProductSolutionEntry;
                        options.push({
                            key: solution.sys.id,
                            name: solution.fields.name,
                            value: solution.sys.id,
                            group: intention.fields.name,
                            icon: solution.fields.icon
                        });
                    }
                }
                return {
                    id: 'product-solution-opportunity',
                    name: 'Maatregel(en)',
                    options: options,
                    field: 'productOptions.sys.id',
                    showButton: true
                };
            }
        );
    }

    async created() {
        // @ts-ignore
        await setMunicipalityState(this.$store, this.$cookies.get(cookieName));
    }

    mounted() {
        this.getfilterDefinition().then(value => {
            this.filterDefinition = value;
        });
    }
}

export default VakSpecialisten;
